// utils/fixedAssetPaymentIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /fixed-assets/{asset_id}/payments, used by
// FixedAssetDetail.tsx -- the only frontend caller of this endpoint.
//
// Supersedes an earlier "two independent refs" design (a bare idempotency
// key ref plus a bare submitted-fingerprint ref). That design retained
// both refs after EVERY failure, with no way to distinguish a definite
// backend rejection (nothing persisted -- see
// backend/services/idempotency.run_idempotent_operation's claim-before-
// create savepoint, which rolls back the claim along with the business
// writes whenever create() raises) from a genuinely ambiguous one (network
// failure, timeout, or HTTP 500 -- which CAN occur after the payment has
// already committed, since backend/fixed_asset_payments.py's
// _load_fixed_asset_payment reload/validation runs after db.commit()).
// That made every definite rejection look identical to an ambiguous one
// to the frontend, permanently blocking a corrected retry as a false
// "edited retry of a possibly-recorded payment". It also had no concept
// of WHICH asset an in-doubt attempt belonged to, since FixedAssetDetail
// is mounted once for the route and reused across different asset_id
// values without remounting.
//
// This module tracks ONE FixedAssetPaymentAttempt value with an explicit
// `state` -- "idle" | "in-flight" | "ambiguous" -- scoped to the asset it
// was created for, so each of the outcomes the endpoint can actually
// produce (confirmed success, definite pre-commit rejection, ambiguous
// outcome) maps to a well-defined transition instead of ad hoc ref
// mutations.
//
// Vendor Payment (utils/vendorPaymentIdempotency.ts) and Credit Card Bill
// Payment (utils/creditCardBillPaymentIdempotency.ts) still use the older
// two-ref design and share the same retain-on-every-failure defect this
// module fixes for Fixed Asset Payment only -- fixing those is a separate,
// out-of-scope follow-up. This file intentionally has no cross-dependency
// on either of their utilities.

export type FixedAssetPaymentPayload = {
  asset_id: number;
  date: string;
  amount: string;
  payment_account_id: number;
  memo: string | null;
};

/**
 * Deterministic canonical payload for POST /fixed-assets/{asset_id}/payments.
 * Callers must pass the SAME object (with idempotency_key added) as the
 * actual request body, never a separately reconstructed copy, so the
 * fingerprint can never drift from what is truly sent.
 *
 * - asset_id: canonicalized to a number, included even though it is also
 *   the path parameter -- the idempotency identity is only (company_id,
 *   operation_type, idempotency_key), so a key reused against a
 *   different asset must still be caught as a conflict. Also used
 *   locally (never sent) to scope an attempt to the asset it belongs to
 *   -- see FixedAssetPaymentAttempt.assetId below.
 * - date: the exact submitted YYYY-MM-DD.
 * - amount: fixed 2-decimal string (matches the backend's q2
 *   quantization), so "10", "10.0", "10.00" all canonicalize identically.
 * - payment_account_id: canonicalized to a number so "12" and 12 never
 *   produce different fingerprints.
 * - memo: trimmed, empty string canonicalized to null -- matching the
 *   backend's own `(payload.get("memo") or "").strip() or None`.
 */
export function canonicalFixedAssetPayment(
  assetId: string | number,
  date: string,
  amount: string | number,
  paymentAccountId: string | number,
  memo: string,
): FixedAssetPaymentPayload {
  return {
    asset_id: Number(assetId),
    date,
    amount: Number(amount).toFixed(2),
    payment_account_id: Number(paymentAccountId),
    memo: memo.trim() || null,
  };
}

export function fingerprintFixedAssetPayment(payload: FixedAssetPaymentPayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt. The next submission always mints a fresh
 *   key.
 * - "in-flight": a request has been sent and its outcome is not yet
 *   known. Transitions to "idle" (success or definite rejection) or
 *   "ambiguous" once the response (or its absence) is classified.
 * - "ambiguous": the last attempt's outcome could not be determined
 *   (network failure, timeout, unreadable response, HTTP 500, or any
 *   other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate; an edited retry, or a retry for a different asset, stays
 *   blocked until this attempt is resolved.
 */
export type FixedAssetPaymentAttemptState = "idle" | "in-flight" | "ambiguous";

export type FixedAssetPaymentAttempt = {
  state: FixedAssetPaymentAttemptState;
  assetId: number | null;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: FixedAssetPaymentPayload | null;
};

export const IDLE_FIXED_ASSET_PAYMENT_ATTEMPT: FixedAssetPaymentAttempt = {
  state: "idle",
  assetId: null,
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type FixedAssetPaymentSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: FixedAssetPaymentAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-cross-asset" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state, the asset being paid, and the
 * canonical payload/fingerprint just built from the form. Never mutates
 * `attempt` -- returns a NEW attempt (state "in-flight") for the caller
 * to store when `kind === "start"`.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME asset, SAME fingerprint -> unchanged retry: reuses
 *    the stored key exactly (never mints a new one for an ambiguous
 *    outcome -- see module docstring).
 *  - ambiguous, SAME asset, DIFFERENT fingerprint -> blocked-mismatch:
 *    the prior attempt under this key may already have committed; an
 *    edited resubmission under the same key must never be sent.
 *  - ambiguous, DIFFERENT asset -> blocked-cross-asset: this component
 *    instance is reused across assets (route param change, no remount --
 *    see FixedAssetDetail.tsx), so a stale ambiguous attempt from a
 *    previously viewed asset must never be silently dropped or reused
 *    for a different one.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decideFixedAssetPaymentAttempt(
  attempt: FixedAssetPaymentAttempt,
  assetId: number,
  payload: FixedAssetPaymentPayload,
  fingerprint: string,
  generateKey: () => string,
): FixedAssetPaymentSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.assetId !== assetId) {
      return { kind: "blocked-cross-asset" };
    }
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      assetId,
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears the
 * attempt back to idle. */
export function resolveFixedAssetPaymentSuccess(): FixedAssetPaymentAttempt {
  return IDLE_FIXED_ASSET_PAYMENT_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400/404 raised by this handler itself, 401/403
 * raised by its auth/authorization dependencies (get_current_user,
 * require_company_admin) before the handler body ever runs, 422 from
 * FastAPI's own request-validation short-circuit, a closed-period 409,
 * or an idempotency-conflict 409. In every one of these cases nothing was
 * persisted under this key (either the handler never ran at all, or
 * create() raised before the claim-insert savepoint could complete --
 * see backend/services/idempotency.run_idempotent_operation), so the
 * identity is free to reuse. Returns to idle so the next submission mints
 * a fresh key and is treated as a new logical attempt, with the user free
 * to edit any field.
 */
export function resolveFixedAssetPaymentDefiniteRejection(): FixedAssetPaymentAttempt {
  return IDLE_FIXED_ASSET_PAYMENT_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so a same-asset unchanged retry safely replays
 * instead of risking a duplicate; an edited retry -- or a retry against a
 * different asset -- stays blocked.
 */
export function resolveFixedAssetPaymentAmbiguousFailure(
  attempt: FixedAssetPaymentAttempt,
): FixedAssetPaymentAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether reopening the payment dialog for `assetId` should restore the
 * previously submitted form values (date/amount/account/memo) instead of
 * resetting to fresh defaults. True only when there is a live AMBIGUOUS
 * attempt for THIS SAME asset.
 *
 * Restoring matters: resetting to fresh defaults (e.g. today's date,
 * the currently-outstanding amount) would almost never coincidentally
 * match what was actually submitted, so the very next submission would
 * compute a different fingerprint and get blocked as an "edited retry" --
 * trapping the user with no visible way to make an unchanged retry that
 * safely reuses the key. See FixedAssetDetail.tsx's openPayment.
 */
export function shouldRestoreAmbiguousAttempt(
  attempt: FixedAssetPaymentAttempt,
  assetId: number,
): boolean {
  return attempt.state === "ambiguous" && attempt.assetId === assetId;
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type FixedAssetPaymentFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection and an idempotency-
 * conflict rejection share HTTP 409 (see
 * backend/utils/accounting_guards.py's _assert_not_closed, and
 * backend/fixed_asset_payments.py's IdempotencyRequestConflict handling)
 * but require different messaging and a different interpretation -- a
 * closed-period 409 is detected via `isClosedPeriod`'s message match; any
 * OTHER 409 is treated as an idempotency conflict, confirmed (by tracing
 * every HTTPException this endpoint's own code and its
 * assert_year_open/post_journal helpers can raise) to be the ONLY other
 * 409 source this endpoint has -- there is no third 409 variant to
 * disambiguate.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400/404 raised directly by this handler, 401/403 raised by
 * get_current_user/require_company_admin (both resolve as FastAPI
 * dependencies BEFORE the handler body runs at all -- see main.py's
 * router registration), and 422 from FastAPI's own RequestValidationError
 * handler (main.py, also short-circuits before the handler runs) -- is
 * treated as a definite rejection uniformly: every one of these occurs
 * strictly before (or in place of) any business write, so nothing was
 * ever persisted under the key. HTTP 408 was investigated specifically
 * (per the audit that produced this classifier): neither this fetch-based
 * apiPost (no client-side timeout/AbortController) nor this backend's
 * ASGI stack (uvicorn/Starlette never emit HTTP_408_REQUEST_TIMEOUT --
 * confirmed by inspecting the installed dependency; it exists only as an
 * unused status-code constant) can produce a parseable 408 response for
 * this endpoint -- a genuine send/receive timeout instead surfaces to the
 * frontend as a fetch-level rejection with no response at all, already
 * covered by the no-numeric-status branch below. No dedicated 408 branch
 * exists because there is no code path that can reach one; if some future
 * infrastructure layer ever injected one, RFC 7231's own definition (the
 * server never received a complete request) makes "definite rejection"
 * the correct classification anyway, and that is exactly what the
 * generic 4xx branch below already does.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response.
 */
export function classifyFixedAssetPaymentFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): FixedAssetPaymentFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // Network failure, timeout, or any other rejection with no HTTP
    // response at all -- outcome unknown, must be treated as ambiguous.
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    // Every other 4xx (400, 401, 403, 404, 422, and any other well-formed
    // 4xx) -- see the module-level rationale above. Deterministic:
    // nothing was ever persisted under this key.
    return "definite-rejection";
  }

  // 5xx (500 and any other), and anything outside the 4xx/409 ranges this
  // app's own code never emits: ambiguous -- the backend's replay loader
  // (_load_fixed_asset_payment) runs AFTER db.commit(), so a 500 can mean
  // the payment WAS persisted and only response construction or replay
  // validation failed. Fails closed for anything not positively
  // identified as a definite pre-commit rejection.
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt for
 * THIS asset remains unresolved. Deliberately does NOT say reloading
 * alone is safe -- a reload proves nothing about whether the prior
 * attempt already posted. Must convey all three ideas: (1) the prior
 * attempt may already have been recorded, (2) restoring the original
 * values permits a safe retry under the same key, (3) the user should
 * check payment history before starting a new payment.
 */
export const FIXED_ASSET_PAYMENT_EDITED_RETRY_BLOCKED_MESSAGE =
  "This payment may already be recorded. Retry with the original details, or check payment history below first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the payment may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const FIXED_ASSET_PAYMENT_CONFLICT_MESSAGE =
  "Couldn't confirm this payment. Check payment history before trying again.";

/**
 * Shown when the user tries to submit a payment for a DIFFERENT asset
 * while an ambiguous attempt from a previously viewed asset is still
 * unresolved (see decideFixedAssetPaymentAttempt's blocked-cross-asset
 * case).
 */
export const FIXED_ASSET_PAYMENT_CROSS_ASSET_BLOCKED_MESSAGE =
  "Another asset has an unconfirmed payment. Retry it there first.";
